import { useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface SearchEvent {
  query: string;
  searchType: 'title' | 'author' | 'isbn';
  bookId: string;
  position: number;
  source: 'local' | 'isbndb' | 'openlibrary';
}

export function useSearchTracking() {
  const { user } = useAuth();
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  const clickTimersRef = useRef<Map<string, number>>(new Map());

  // Track search impression (when results are shown)
  const trackImpression = useCallback(async (event: SearchEvent) => {
    const queryNormalized = event.query.toLowerCase().trim().replace(/\s+/g, ' ');
    
    try {
      await supabase.from('search_events').insert({
        user_id: user?.id || null,
        session_id: sessionIdRef.current,
        query: event.query,
        query_normalized: queryNormalized,
        search_type: event.searchType,
        result_book_id: event.bookId,
        result_position: event.position,
        result_source: event.source,
        clicked: false,
      });

      // Start timer for time-to-click tracking
      clickTimersRef.current.set(event.bookId, Date.now());
    } catch (error) {
      console.error('Failed to track search impression:', error);
    }
  }, [user?.id]);

  // Track when user clicks a search result
  const trackClick = useCallback(async (bookId: string) => {
    const clickTime = Date.now();
    const impressionTime = clickTimersRef.current.get(bookId);
    const timeToClick = impressionTime ? clickTime - impressionTime : null;

    try {
      // Update the most recent impression for this book in this session
      const { error } = await supabase
        .from('search_events')
        .update({ 
          clicked: true,
          time_to_click_ms: timeToClick 
        })
        .eq('session_id', sessionIdRef.current)
        .eq('result_book_id', bookId)
        .eq('clicked', false)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      // Clean up timer
      clickTimersRef.current.delete(bookId);
    } catch (error) {
      console.error('Failed to track click:', error);
    }
  }, []);

  // Track when user adds book to TBR from search
  const trackTBRAdd = useCallback(async (bookId: string) => {
    try {
      await supabase
        .from('search_events')
        .update({ added_to_tbr: true })
        .eq('session_id', sessionIdRef.current)
        .eq('result_book_id', bookId)
        .order('created_at', { ascending: false })
        .limit(1);
    } catch (error) {
      console.error('Failed to track TBR add:', error);
    }
  }, []);

  // Track when user rates a book from search
  const trackRating = useCallback(async (bookId: string) => {
    try {
      await supabase
        .from('search_events')
        .update({ rated: true })
        .eq('session_id', sessionIdRef.current)
        .eq('result_book_id', bookId)
        .order('created_at', { ascending: false })
        .limit(1);
    } catch (error) {
      console.error('Failed to track rating:', error);
    }
  }, []);

  // Track trending searches
  const trackTrendingSearch = useCallback(async (query: string) => {
    const queryNormalized = query.toLowerCase().trim().replace(/\s+/g, ' ');
    
    try {
      // Try to increment existing trending search
      const { data: existing } = await supabase
        .from('trending_searches')
        .select('id, search_count')
        .eq('query_normalized', queryNormalized)
        .single();

      if (existing) {
        await supabase
          .from('trending_searches')
          .update({ 
            search_count: existing.search_count + 1,
            last_searched_at: new Date().toISOString()
          })
          .eq('id', existing.id);
      } else {
        // Create new trending search entry
        await supabase
          .from('trending_searches')
          .insert({
            query: query,
            query_normalized: queryNormalized,
            search_count: 1,
            unique_users: 1,
          });
      }
    } catch (error) {
      // Silently fail for trending searches (non-critical)
      console.debug('Failed to track trending search:', error);
    }
  }, []);

  return {
    trackImpression,
    trackClick,
    trackTBRAdd,
    trackRating,
    trackTrendingSearch,
  };
}
